import { useState, useEffect, useCallback } from "react";
import { useAuthFetch } from "@/hooks/useAuthFetch";
import { InviteModal } from "./InviteModal";
import type { InterviewInvitation } from "@acde/shared";

const API_URL = import.meta.env.VITE_API_URL || "/api";

interface InviteeTrackerProps {
  interviewId: string;
  interviewTitle: string;
  shareToken: string;
}

export function InviteeTracker({
  interviewId,
  interviewTitle,
  shareToken,
}: InviteeTrackerProps) {
  const { authFetch } = useAuthFetch();
  const [invitations, setInvitations] = useState<InterviewInvitation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);

  const loadInvitations = useCallback(async () => {
    try {
      const res = await authFetch(
        `${API_URL}/interviews/${interviewId}/invitations`
      );
      if (res.ok) {
        const data = await res.json();
        setInvitations(data.invitations || []);
      }
    } catch {
      // silently fail
    } finally {
      setIsLoading(false);
    }
  }, [interviewId, authFetch]);

  useEffect(() => {
    loadInvitations();
  }, [loadInvitations]);

  const handleDelete = async (invitationId: string) => {
    try {
      const res = await authFetch(
        `${API_URL}/interviews/${interviewId}/invitations/${invitationId}`,
        { method: "DELETE" }
      );
      if (res.ok) {
        setInvitations((prev) => prev.filter((i) => i.id !== invitationId));
      }
    } catch {
      // silently fail
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-NZ", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const statusConfig = {
    invited: {
      label: "Invited",
      bg: "bg-gray-100 dark:bg-gray-800",
      text: "text-gray-600 dark:text-gray-400",
    },
    in_progress: {
      label: "In Progress",
      bg: "bg-yellow-100 dark:bg-yellow-900/30",
      text: "text-yellow-700 dark:text-yellow-400",
    },
    completed: {
      label: "Completed",
      bg: "bg-green-100 dark:bg-green-900/30",
      text: "text-green-700 dark:text-green-400",
    },
  };

  const completedCount = invitations.filter(
    (i) => i.status === "completed"
  ).length;

  return (
    <>
      <div className="card mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-foreground">
              Track Invitees
            </h3>
            {invitations.length > 0 && (
              <span className="px-2 py-0.5 text-xs rounded-full bg-primary/10 text-primary font-medium">
                {completedCount}/{invitations.length}
              </span>
            )}
          </div>
          <button
            onClick={() => setShowInviteModal(true)}
            className="btn btn-primary text-sm flex items-center justify-center gap-1.5 w-full sm:w-auto"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
              />
            </svg>
            Invite People
          </button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : invitations.length === 0 ? (
          <div className="text-center py-8">
            <svg
              className="w-10 h-10 mx-auto text-muted mb-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
            <p className="text-sm text-muted mb-3">
              No invitations sent yet.
            </p>
            <button
              onClick={() => setShowInviteModal(true)}
              className="text-sm text-primary hover:underline"
            >
              Invite people to get started
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {invitations.map((invitation) => {
              const cfg = statusConfig[invitation.status];
              return (
                <div
                  key={invitation.id}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-user-msg/50 hover:bg-user-msg transition-colors group"
                >
                  {/* Checkbox indicator */}
                  <div
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                      invitation.status === "completed"
                        ? "bg-green-500 border-green-500"
                        : "border-border"
                    }`}
                  >
                    {invitation.status === "completed" && (
                      <svg
                        className="w-3 h-3 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={3}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    )}
                  </div>

                  {/* Avatar */}
                  <div className="w-8 h-8 rounded-full bg-primary/10 hidden sm:flex items-center justify-center shrink-0">
                    <span className="text-xs font-semibold text-primary">
                      {(invitation.name || invitation.email)
                        .split(/[\s@]/)
                        .map((n) => n[0])
                        .join("")
                        .slice(0, 2)
                        .toUpperCase()}
                    </span>
                  </div>

                  {/* Details */}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-foreground truncate">
                      {invitation.name || invitation.email}
                    </div>
                    {invitation.name && (
                      <div className="text-xs text-muted truncate">
                        {invitation.email}
                      </div>
                    )}
                  </div>

                  {/* Status */}
                  <span
                    className={`px-2 py-0.5 text-xs rounded-full font-medium ${cfg.bg} ${cfg.text}`}
                  >
                    {cfg.label}
                  </span>

                  {/* Invited date */}
                  <span className="text-xs text-muted hidden sm:inline">
                    {formatDate(invitation.invitedAt)}
                  </span>

                  {/* Remove button */}
                  <button
                    onClick={() => handleDelete(invitation.id)}
                    className="w-6 h-6 rounded flex items-center justify-center sm:opacity-0 sm:group-hover:opacity-100 hover:bg-red-100 dark:hover:bg-red-900/30 transition-all"
                    title="Remove invitation"
                  >
                    <svg
                      className="w-3.5 h-3.5 text-red-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <InviteModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        interviewId={interviewId}
        interviewTitle={interviewTitle}
        shareToken={shareToken}
        onInvitesSent={loadInvitations}
      />
    </>
  );
}
